import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

import { Button } from '@aurum/ui';

import { api, type LoyaltyAccount, type OrderSummary } from '../../lib/api';

import { useCartStore, type CartLine } from './cart-store';
import { useTabletSession } from './useTabletSession';

const USE_FIXTURES: boolean = String(import.meta.env.VITE_USE_FIXTURES ?? 'false') === 'true';

export function CartDrawer(): JSX.Element {
  const { lines, isOpen, close, setQuantity, remove, subtotal, clear } = useCartStore();
  const session = useTabletSession();

  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    summary: OrderSummary;
    pointsEarned: number | null;
  } | null>(null);

  async function placeOrder(): Promise<void> {
    if (lines.length === 0) return;
    setSubmitting(true);
    setError(null);

    if (USE_FIXTURES) {
      await new Promise((r) => setTimeout(r, 700));
      const total = subtotal() * 1.15;
      const fakeOrder: OrderSummary = {
        id: 'demo-' + Date.now().toString(36),
        orderNumber: 1000 + Math.floor(Math.random() * 9000),
        status: 'placed',
        total,
        currency: lines[0]?.currency ?? 'OMR',
      };
      const pointsEarned = Math.floor(total * 10 * 1.25); // Silver tier multiplier
      clear();
      setConfirmation({ summary: fakeOrder, pointsEarned });
      setSubmitting(false);
      return;
    }

    const customerId = session.customerId ?? undefined;
    const balanceBefore = customerId
      ? await readBalance(customerId)
      : null;

    try {
      const summary = await api.placeOrder({
        branchId: session.branchId,
        tableId:  session.tableId,
        customerId,
        items: lines.map((l) => ({
          menuItemId: l.menuItemId,
          quantity:   l.quantity,
          specialInstructions: serializeCustomizations(l.customizations),
        })),
      });

      let pointsEarned: number | null = null;
      if (customerId && balanceBefore !== null) {
        const after = await readBalance(customerId);
        if (after !== null) pointsEarned = after - balanceBefore;
      }

      clear();
      setConfirmation({ summary, pointsEarned });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Order failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {isOpen ? (
        <>
          <motion.div
            key="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-30 bg-onyx-900/70 backdrop-blur-sm"
            onClick={close}
            aria-hidden
          />
          <motion.aside
            key="cart-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 240, damping: 30 }}
            className="fixed inset-y-0 left-0 z-40 flex w-full max-w-md flex-col border-r border-aurum-500/20 bg-onyx-700 shadow-aurum-deep"
            role="dialog"
            aria-modal="true"
          >
            <header className="flex items-baseline justify-between border-b border-aurum-500/15 px-6 py-5">
              <h2 className="font-display text-2xl text-aurum-200">طلبك</h2>
              <button
                onClick={close}
                className="rounded-aurum-sm border border-aurum-500/30 px-3 py-1 text-xs text-cream-100/80 hover:bg-aurum-500/10"
              >
                إغلاق
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {confirmation ? (
                <Confirmation
                  summary={confirmation.summary}
                  pointsEarned={confirmation.pointsEarned}
                  onReset={() => setConfirmation(null)}
                />
              ) : lines.length === 0 ? (
                <p className="mt-10 text-center font-body text-sm text-cream-100/60">
                  السلة فارغة. اختر صنفاً من القائمة لتبدأ.
                </p>
              ) : (
                <ul className="space-y-3">
                  {lines.map((line) => (
                    <CartLineRow
                      key={line.lineId}
                      line={line}
                      onIncrement={() => setQuantity(line.lineId, line.quantity + 1)}
                      onDecrement={() => setQuantity(line.lineId, line.quantity - 1)}
                      onRemove={() => remove(line.lineId)}
                    />
                  ))}
                </ul>
              )}
            </div>

            {!confirmation && lines.length > 0 ? (
              <footer className="border-t border-aurum-500/15 px-6 py-5">
                <div className="mb-3 flex items-baseline justify-between font-body text-sm text-cream-100/80">
                  <span>المجموع الفرعي</span>
                  <span className="font-display text-xl text-aurum-200">
                    {subtotal().toFixed(3)} {lines[0]?.currency ?? ''}
                  </span>
                </div>
                <p className="mb-3 font-body text-xs text-onyx-100">
                  يُضاف ضريبة القيمة المضافة (15٪) عند تأكيد الطلب.
                </p>
                {error ? (
                  <p className="mb-3 rounded-aurum-sm border border-accent-ruby/40 bg-accent-ruby/10 p-2 text-xs text-accent-ruby">
                    {error}
                  </p>
                ) : null}
                <Button
                  size="lg"
                  className="w-full"
                  isLoading={submitting}
                  onClick={() => void placeOrder()}
                >
                  تأكيد الطلب
                </Button>
              </footer>
            ) : null}
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}

async function readBalance(userId: string): Promise<number | null> {
  try {
    const a: LoyaltyAccount = await api.getLoyalty(userId);
    return a.pointsBalance;
  } catch {
    return null;
  }
}

function CartLineRow({
  line,
  onIncrement,
  onDecrement,
  onRemove,
}: {
  line: CartLine;
  onIncrement: () => void;
  onDecrement: () => void;
  onRemove: () => void;
}): JSX.Element {
  return (
    <li className="rounded-aurum-md border border-aurum-500/15 bg-onyx-900/40 p-4">
      <div className="flex items-baseline justify-between">
        <p className="font-display text-lg text-aurum-200">{line.nameAr}</p>
        <p className="font-display text-sm text-aurum-300">
          {(line.unitPrice * line.quantity).toFixed(3)} {line.currency}
        </p>
      </div>
      <p className="mt-1 font-body text-xs uppercase tracking-widest text-aurum-400">
        {line.nameEn}
      </p>
      {Object.keys(line.customizations).length > 0 ? (
        <p className="mt-2 font-body text-xs text-cream-100/70">
          {Object.entries(line.customizations).map(([k, v]) => k + '=' + v).join(' · ')}
        </p>
      ) : null}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={onDecrement}
            className="h-8 w-8 rounded-aurum-sm border border-aurum-500/30 text-cream-100 hover:bg-aurum-500/10"
          >
            −
          </button>
          <span className="w-6 text-center font-mono text-sm">{line.quantity}</span>
          <button
            onClick={onIncrement}
            className="h-8 w-8 rounded-aurum-sm border border-aurum-500/30 text-cream-100 hover:bg-aurum-500/10"
          >
            +
          </button>
        </div>
        <button
          onClick={onRemove}
          className="text-xs text-accent-ruby/80 hover:text-accent-ruby"
        >
          إزالة
        </button>
      </div>
    </li>
  );
}

function Confirmation({
  summary,
  pointsEarned,
  onReset,
}: {
  summary: OrderSummary;
  pointsEarned: number | null;
  onReset: () => void;
}): JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      className="mt-6 space-y-4 text-center"
    >
      <p className="aurum-gradient-text font-display text-3xl">شكراً لك</p>
      <p className="font-body text-sm text-cream-100/80">
        طلبك رقم{' '}
        <span className="font-mono text-aurum-300">#{summary.orderNumber}</span>{' '}
        قيد التحضير الآن.
      </p>
      <div className="rounded-aurum-md border border-aurum-500/20 bg-onyx-900/40 p-4 font-display">
        <p className="text-xs uppercase tracking-widest text-onyx-100">المجموع</p>
        <p className="mt-1 text-2xl text-aurum-200">
          {summary.total.toFixed(3)} {summary.currency}
        </p>
      </div>
      {pointsEarned !== null && pointsEarned > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="rounded-aurum-md border border-aurum-500/30 bg-aurum-500/10 p-4"
        >
          <p className="font-display text-lg text-aurum-200">
            +{pointsEarned} نقطة
          </p>
          <p className="font-body text-xs text-aurum-300/80">
            أُضيفت إلى حسابك في برنامج الولاء.
          </p>
        </motion.div>
      ) : null}
      <Button onClick={onReset} variant="ghost" className="w-full">
        طلب جديد
      </Button>
    </motion.div>
  );
}

function serializeCustomizations(c: Record<string, string>): string | undefined {
  const parts = Object.entries(c).filter(([, v]) => v && v !== '');
  if (parts.length === 0) return undefined;
  return parts.map(([k, v]) => k + '=' + v).join('; ');
}
